// Q&A-Dashboard für die Praxis.
// Liste links, Detail-Konversation rechts (zweispaltige Ansicht).

import { useEffect, useState } from 'react';
import AuthImage from '../components/AuthImage';
import {
  closeFrage,
  getFrage,
  listFragen,
  replyFrage,
  vorschlagTermin,
  type FrageThread,
} from '../api';

export default function FragenPage() {
  const [threads, setThreads] = useState<(FrageThread & { patient?: { id: string; name: string } })[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [, setLoading] = useState(true);
  const [filter, setFilter] = useState<'alle' | 'offen' | 'beantwortet' | 'geschlossen'>('offen');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listFragen();
      setThreads(r.threads);
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = threads.filter((t) => filter === 'alle' || t.status === filter);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Fragen</h1>
          <p className="muted">Patientenfragen zu Beschwerden oder Beratungs-Rezepten. Antworten gehen sofort via Push an den Patienten.</p>
        </div>
        <button className="btn-secondary" onClick={reload}>↻ Neu laden</button>
      </div>

      <div className="toolbar">
        <div className="tabs">
          {(['offen', 'beantwortet', 'geschlossen', 'alle'] as const).map((s) => {
            const count = s === 'alle' ? threads.length : threads.filter((t) => t.status === s).length;
            return (
              <button
                key={s}
                className={`tab ${filter === s ? 'active' : ''}`}
                onClick={() => setFilter(s)}
              >
                {labelFor(s)} ({count})
              </button>
            );
          })}
        </div>
      </div>

      <div className="fragen-split">
        <aside className="fragen-list">
          {filtered.length === 0 ? (
            <div className="empty-card">
              <p className="muted">Keine Fragen in dieser Kategorie.</p>
            </div>
          ) : (
            filtered.map((t) => (
              <button
                key={t.id}
                className={`thread-card ${selected === t.id ? 'selected' : ''} ${t.ungelesen_count > 0 ? 'unread' : ''}`}
                onClick={() => setSelected(t.id)}
              >
                <div className="thread-head">
                  <strong>{t.titel}</strong>
                  {t.ungelesen_count > 0 && <span className="badge-unread">{t.ungelesen_count}</span>}
                </div>
                <div className="muted small">{t.patient?.name ?? 'Unbekannt'}</div>
                <div className="muted xs">{new Date(t.updated_at).toLocaleString('de-DE')}</div>
              </button>
            ))
          )}
        </aside>

        <section className="fragen-detail">
          {selected ? (
            <ThreadDetail
              threadId={selected}
              onUpdated={reload}
              onClose={() => setSelected(null)}
            />
          ) : (
            <div className="empty-card centered">
              <div className="empty-icon">💬</div>
              <p>Wähle links eine Frage aus, um zu antworten.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function labelFor(s: 'offen' | 'beantwortet' | 'geschlossen' | 'alle'): string {
  return s === 'offen' ? 'Offen' : s === 'beantwortet' ? 'Beantwortet' : s === 'geschlossen' ? 'Geschlossen' : 'Alle';
}

function InlineTerminVorschlag({
  patientId,
  threadId,
  patientName,
  onClose,
  onSent,
}: {
  patientId: string;
  threadId: string;
  patientName: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const [datum, setDatum] = useState(tomorrow);
  const [uhrzeit, setUhrzeit] = useState('09:00');
  const [notiz, setNotiz] = useState('');
  const [sending, setSending] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await vorschlagTermin(patientId, {
        termin_datum: datum,
        termin_uhrzeit: uhrzeit,
        notiz: notiz.trim() || undefined,
        thread_id: threadId,
      });
      onSent();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} style={{
      background: '#FFF4E5', borderTop: '1px solid var(--border)', padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ fontSize: 13, color: '#B85C00', fontWeight: 600 }}>
        Termin für {patientName} vorschlagen
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="date"
          value={datum}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setDatum(e.target.value)}
          required
          style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
        />
        <input
          type="time"
          value={uhrzeit}
          onChange={(e) => setUhrzeit(e.target.value)}
          required
          style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
        />
        <input
          type="text"
          placeholder="Notiz (optional)"
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          maxLength={1000}
          style={{ flex: 1, minWidth: 200, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6 }}
        />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button type="submit" className="btn-primary" disabled={sending}>
          {sending ? 'Sende…' : 'Vorschlag senden (mit Push)'}
        </button>
      </div>
    </form>
  );
}

function ThreadDetail({
  threadId,
  onUpdated,
  onClose,
}: {
  threadId: string;
  onUpdated: () => void;
  onClose: () => void;
}) {
  const [data, setData] = useState<{
    thread: FrageThread;
    patient: { id: string; vorname: string; nachname: string; geburtsdatum: string; email: string } | null;
  } | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [terminOpen, setTerminOpen] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await getFrage(threadId);
      setData({ thread: r.thread, patient: r.patient });
    } catch (err: any) {
      if (!silent) alert('Fehler: ' + err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);

  // Auto-Refresh alle 8 Sekunden, solange Thread offen ist
  useEffect(() => {
    if (!data || data.thread.status === 'geschlossen') return;
    const handle = setInterval(() => load(true), 8000);
    return () => clearInterval(handle);
    // eslint-disable-next-line
  }, [data?.thread.status, threadId]);

  const onSend = async () => {
    if (!reply.trim() || !data) return;
    setSending(true);
    try {
      const r = await replyFrage(threadId, reply.trim());
      setData({ ...data, thread: r.thread });
      setReply('');
      onUpdated();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const onCloseThread = async () => {
    if (!confirm('Thread schließen? Der Patient kann dann keine weiteren Nachrichten senden.')) return;
    try {
      await closeFrage(threadId);
      onUpdated();
      onClose();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    }
  };

  if (loading || !data) return <div style={{ padding: 40, textAlign: 'center' }}>Lädt…</div>;

  const closed = data.thread.status === 'geschlossen';

  return (
    <div className="thread-detail">
      <header className="thread-header">
        <div>
          <h2 style={{ margin: 0 }}>{data.thread.titel}</h2>
          {data.patient && (
            <div className="muted small">
              {data.patient.vorname} {data.patient.nachname} (geb. {data.patient.geburtsdatum}) · {data.patient.email}
            </div>
          )}
        </div>
        <div className="row">
          {!closed && data.patient && (
            <button className="btn-primary" onClick={() => setTerminOpen(true)}>📅 Termin vorschlagen</button>
          )}
          {!closed && <button className="btn-secondary" onClick={onCloseThread}>Schließen</button>}
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
      </header>

      {terminOpen && data.patient && (
        <InlineTerminVorschlag
          patientId={data.patient.id}
          threadId={data.thread.id}
          patientName={`${data.patient.vorname} ${data.patient.nachname}`}
          onClose={() => setTerminOpen(false)}
          onSent={() => { setTerminOpen(false); load(); onUpdated(); }}
        />
      )}

      <div className="thread-messages">
        {data.thread.messages.map((m) => (
          <div key={m.id} className={`msg msg-${m.from}`}>
            {m.attachments && m.attachments.length > 0 && (
              <div className="msg-attachments">
                {m.attachments.map((aid) => (
                  <a
                    key={aid}
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      // Bild in neuem Fenster anzeigen — Blob-URL Workaround
                      const res = await fetch(`${(import.meta.env.VITE_API_BASE_URL as string ?? 'https://api.bertsy.de')}/api/uploads/${aid}`, {
                        headers: { Authorization: `Bearer ${sessionStorage.getItem('bertsy_praxis_session') ?? ''}` },
                      });
                      const blob = await res.blob();
                      window.open(URL.createObjectURL(blob), '_blank');
                    }}
                  >
                    <AuthImage id={aid} alt="Foto" style={{
                      maxWidth: 180, maxHeight: 180, borderRadius: 8, display: 'block',
                      cursor: 'pointer',
                    }} />
                  </a>
                ))}
              </div>
            )}
            {m.text && <div className="msg-bubble">{m.text}</div>}
            <div className="msg-meta">
              {m.from === 'patient' ? 'Patient' : 'Sie'} · {new Date(m.ts).toLocaleString('de-DE')}
              {m.gelesen_am && m.from === 'praxis' && <span> · gelesen</span>}
            </div>
          </div>
        ))}
      </div>

      {closed ? (
        <div className="thread-closed">Thread geschlossen.</div>
      ) : (
        <div className="thread-compose">
          <textarea
            placeholder="Antwort schreiben… (Enter = senden, Shift+Enter = neue Zeile)"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              // Enter zum Senden, Shift+Enter für neue Zeile
              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                if (reply.trim() && !sending) onSend();
              }
            }}
            maxLength={5000}
            rows={3}
          />
          <button className="btn-primary" onClick={onSend} disabled={!reply.trim() || sending}>
            {sending ? 'Sende…' : 'Antwort senden'}
          </button>
        </div>
      )}
    </div>
  );
}
